import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Cross from 'aquilla/icons/misc/Cross';
import AquillaButton from 'aquilla/core/molecules/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogHeading
} from 'aquilla/core/molecules/Dialog';
import fetch from '@api/fetchers/index';
import Input from 'aquilla/core/molecules/Input';
import { useEffect, useState } from 'react';
import Typography from 'aquilla/core/atoms/Typography';
import { segment } from '@actions/analytics';
import MultiCheckBox from './MultiCheckBox';
import { getReferralExitIntentEndpoint } from '../apiHelper';
import { REFER_EXIT_INTENT_SUBMITTED, STATUS } from '../../constants';

const StyDialog = styled(Dialog)`
  padding-bottom: ${Spacings.SPACING_6B};
`;

const StyDialogHeader = styled(DialogHeader)`
  padding-bottom: ${Spacings.SPACING_2B};
`;

const InputWrapper = styled.div`
  margin-top: 18px;
`;

const DialogSubHeading = styled(Typography)`
  color: var(--color-text-secondary);
  margin-bottom: ${Spacings.SPACING_6B};
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: ${Spacings.SPACING_6B};
`;

const StyButton = styled(AquillaButton)`
  width: 218px;
`;

const Error = styled(Typography)`
  font-weight: ${Fonts.HEAVY};
  color: var(--color-i-red);
  text-align: center;
  justify-content: center;
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: -${Spacings.SPACING_3B};
`;

const ReferralFeedbackDialog = ({
  setOpenFeedbackDialog,
  openFeedbackDialog,
  setFeedBackData,
  feedBackData,
  referralFeedbackData,
  routeUrl,
  hasError,
  setHasError,
  segmentData,
  goalSegmentData,
  setShowToast
}) => {
  const [inputData, setInputData] = useState('');
  const isOtherOption = feedBackData?.find(
    (option: string) => option === 'Other reason'
  );
  useEffect(() => {
    if (isOtherOption) {
      const input = document.getElementsByTagName('textarea');
      if (input) input[0].focus();
    }
  }, [isOtherOption]);

  const sendData = ({ data }) => {
    fetch(getReferralExitIntentEndpoint(), {
      requestType: 'POST',
      body: data
    });
  };

  const closeDialog = () => {
    setOpenFeedbackDialog(false);
    document.body.style.overflow = 'unset'; // This is temporary fix and it should be handled from Aquilla's Dialog
    window.open(routeUrl, '_self');
  };

  const onChange = (option: any) => {
    const feedBackObj = [...feedBackData];
    const index = feedBackObj.indexOf(option);
    if (index > -1) {
      feedBackObj.splice(index, 1);
    } else {
      feedBackObj.push(option);
    }
    setFeedBackData(feedBackObj);
    setHasError(null);
  };

  const onSkip = () => {
    const data = {
      status: STATUS.skip,
      feedback_text: ''
    };
    sendData({ data });
    closeDialog();
  };

  const onSubmit = () => {
    if (!feedBackData.length) {
      setHasError('select');
    } else if (isOtherOption && !inputData.length) {
      setHasError('empty');
    } else {
      segment.track(REFER_EXIT_INTENT_SUBMITTED, {
        ...goalSegmentData,
        is_referred_a_learner: segmentData.is_referred_a_learner,
        total_referrals: segmentData.total_referrals,
        voucher_amount: segmentData.voucher_amount,
        total_extension: segmentData.total_extension,
        referral_type: segmentData.referral_type,
        options_selected: feedBackData,
        feedback: inputData
      });
      let feedbackText = '';
      feedBackData.forEach((element: string) => {
        if (feedbackText !== '') {
          if (element === 'Other reason') {
            feedbackText += `, ${element} : ${inputData}`;
          } else {
            feedbackText += `, ${element}`;
          }
        } else if (element === 'Other reason') {
          feedbackText = `${feedbackText}, ${element} : ${inputData}`;
        } else {
          feedbackText = element;
        }
      });
      const data = {
        status: STATUS.submit,
        feedback_text: feedbackText
      };
      sendData({ data });
      setShowToast(true);
      closeDialog();
    }
  };

  const onInputChange = (e: { target: { value: any } }) => {
    const input = e.target.value;
    setInputData(input);
    setHasError(null);
  };

  const showInput = feedBackData?.includes('Other reason');

  const getErrorText = () => {
    if (hasError === 'select') {
      return 'Please select an option from the list';
    }
    if (hasError === 'empty') {
      return 'Kindly specify what did not work out';
    }
  };

  return (
    <StyDialog open={openFeedbackDialog} size="small">
      <StyDialogHeader>
        <Cross onClick={onSkip} height={24} width={24} />
        <DialogHeading>Didn’t get what you were looking for?</DialogHeading>
      </StyDialogHeader>
      <DialogContent>
        <>
          <DialogSubHeading variant="p2">
            Help us understand how we can make referrals better
          </DialogSubHeading>
          <MultiCheckBox
            onChange={onChange}
            referralFeedbackData={referralFeedbackData}
            feedBackData={feedBackData}
          />
          {showInput && (
            <InputWrapper>
              <Input
                title="Kindly specify your reason"
                placeholder="Please let us know how can we make it better"
                value={inputData}
                multiLine
                rows={3}
                cols={56}
                onChange={onInputChange}
              />
            </InputWrapper>
          )}
          {hasError && <Error variant="p2">{getErrorText()}</Error>}
          <ButtonWrapper>
            <StyButton variant="secondary" size="large" onClick={onSkip}>
              Skip
            </StyButton>
            <StyButton size="large" onClick={onSubmit}>
              Submit
            </StyButton>
          </ButtonWrapper>
        </>
      </DialogContent>
    </StyDialog>
  );
};

export default ReferralFeedbackDialog;
